<template>
  <div class="alerts-dialog__backdrop" @click.self="requestClose">
    <div
      ref="box"
      class="alerts-dialog"
      :class="{ 'alerts-dialog--wide': wide }"
      :role="role"
      aria-modal="true"
      :aria-labelledby="titleId"
      :aria-describedby="describedBy || undefined"
      tabindex="-1"
      @keydown.esc.stop.prevent="requestClose"
      @keydown.tab="trapFocus"
    >
      <h3 :id="titleId" class="alerts-dialog__title">{{ title }}</h3>
      <div v-if="subtitle" class="alerts-dialog__sub">{{ subtitle }}</div>
      <div ref="body" class="alerts-dialog__body">
        <slot />
      </div>
      <div v-if="$slots.actions" class="alerts-dialog__actions">
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

// The Alerts workspace's one dialog shell (design brief §5 "Modals"): the
// keyboard contract `ServerDetailPanel.vue`'s confirm modal established in
// c2ee582, made reusable —
//  - `role="dialog"` for forms, `role="alertdialog"` for confirmations;
//    `aria-modal`, labelled by the title;
//  - Escape = Cancel (emits `close`), a backdrop click too;
//  - Tab / Shift+Tab cycle inside the dialog only;
//  - focus on open: an element marked `data-autofocus`, else (confirm) the
//    first action button — Cancel by convention — or (form) the first field;
//  - focus returns to whatever had it when the dialog opened.
// The parent mounts it with `v-if` and unmounts it on `close`.
const props = defineProps({
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
  role: { type: String, default: 'dialog', validator: (v) => ['dialog', 'alertdialog'].includes(v) },
  wide: { type: Boolean, default: false },
  describedBy: { type: String, default: '' },
});
const emit = defineEmits(['close']);

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

const titleId = `alerts-dialog-title-${Math.random().toString(36).slice(2, 10)}`;
const box = ref(null);
const body = ref(null);
let returnFocusTo = null;

function focusables() {
  if (!box.value) return [];
  return [...box.value.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
}

function trapFocus(event) {
  const items = focusables();
  if (!items.length) {
    event.preventDefault();
    box.value?.focus();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && (active === first || active === box.value)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (active === last || active === box.value)) {
    event.preventDefault();
    first.focus();
  }
}

function requestClose() {
  emit('close');
}

onMounted(() => {
  returnFocusTo = document.activeElement;
  nextTick(() => {
    const root = box.value;
    if (!root) return;
    const marked = root.querySelector('[data-autofocus]');
    let target = marked;
    if (!target && props.role === 'alertdialog') {
      target = root.querySelector('.alerts-dialog__actions button');
    }
    if (!target) {
      target = body.value?.querySelector(FOCUSABLE) || root.querySelector(FOCUSABLE);
    }
    (target || root).focus();
  });
});

onBeforeUnmount(() => {
  const to = returnFocusTo;
  returnFocusTo = null;
  if (to && to.isConnected && typeof to.focus === 'function') {
    nextTick(() => to.focus());
  }
});
</script>

<style lang="scss">
.alerts-dialog__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(14, 46, 48, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.alerts-dialog {
  background: var(--theme-bg-card);
  border-radius: var(--theme-radius-lg, 10px);
  padding: 20px 22px 18px;
  width: 400px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  box-shadow: var(--theme-shadow-panel, var(--theme-shadow-card));
  outline: none;

  &--wide {
    width: 560px;
  }

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }

  &__sub {
    margin: 3px 0 0;
    font-size: 12px;
    color: var(--theme-text-secondary);
    font-family: var(--theme-font-mono);
  }

  &__body {
    margin-top: 14px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
    flex-wrap: wrap;
  }

  @media (max-width: 600px) {
    width: 100vw;
    max-width: none;
    height: 100vh;
    max-height: none;
    border-radius: 0;
  }
}
</style>
